/**
 * Application store slice for global app configuration and feature flags
 * Manages application-wide settings, configuration, and feature toggles
 */

import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { ApplicationStore, AppConfig, FeatureFlags } from '../types'
import { devtoolsDev } from '../middleware'

/**
 * Default feature flags
 */
const defaultFeatureFlags: FeatureFlags = {
  darkMode: true,
  advancedSearch: true,
  analytics: true,
  betaFeatures: false,
  aiAssistant: false
}

/**
 * Default application configuration
 */
const defaultAppConfig: AppConfig = {
  apiUrl: import.meta.env.VITE_API_URL || 'https://store.no-downtime.com/api',
  version: '1.0.0',
  environment: (import.meta.env.NODE_ENV as AppConfig['environment']) || 'development',
  maintenanceMode: false,
  supportEmail: 'support@taqat.ai',
  documentsUrl: '/docs'
}

/**
 * Initial application state
 */
const initialApplicationState = {
  config: defaultAppConfig,
  features: defaultFeatureFlags,
  initialized: false
}

/**
 * Creates the application store with actions
 */
const createApplicationStore = () => create<ApplicationStore>()(
  devtoolsDev(
    // @ts-ignore
    persist(
      immer((set) => ({
        // Initial state
        ...initialApplicationState,

        // Configuration actions
        updateConfig: (config: Partial<AppConfig>) => {
          set((state) => {
            state.config = { ...state.config, ...config }
          })
        },

        // Feature flag actions
        toggleFeature: (feature: keyof FeatureFlags, enabled?: boolean) => {
          set((state) => {
            if (enabled !== undefined) {
              state.features[feature] = enabled
            } else {
              state.features[feature] = !state.features[feature]
            }
          })
        },

        // Initialization actions
        setInitialized: (initialized: boolean) => {
          set((state) => {
            state.initialized = initialized
          })
        },

        // Reset actions
        resetApplication: () => {
          set((state) => {
            state.config = defaultAppConfig
            state.features = defaultFeatureFlags
            state.initialized = false
          })
        }
      })),
      {
        name: 'taqat-application-store',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          features: state.features,
          // Only persist user-configurable config, not environment-specific config
          config: {
            maintenanceMode: state.config.maintenanceMode
          }
        })
      }
    ),
    'ApplicationStore'
  )
)

/**
 * Application store instance
 */
export const applicationStore = createApplicationStore()

/**
 * Application store hook for components
 * @returns Application store state and actions
 */
export const useApplicationStore = () => applicationStore()

/**
 * Hook for application configuration
 * @returns Application config and config actions
 */
export const useAppConfig = () => {
  const { config, updateConfig } = useApplicationStore()
  
  return {
    config,
    updateConfig,
    // Convenience methods
    setMaintenanceMode: (enabled: boolean) => updateConfig({ maintenanceMode: enabled }),
    setSupportEmail: (email: string) => updateConfig({ supportEmail: email }),
    setDocumentsUrl: (url: string) => updateConfig({ documentsUrl: url })
  }
}

/**
 * Hook for feature flags management
 * @returns Feature flags and feature toggle actions
 */
export const useFeatureFlags = () => {
  const { features, toggleFeature } = useApplicationStore()
  
  return {
    features,
    toggleFeature,
    // Convenience methods for specific features
    toggleDarkMode: () => toggleFeature('darkMode'),
    toggleAdvancedSearch: () => toggleFeature('advancedSearch'),
    toggleAnalytics: () => toggleFeature('analytics'),
    toggleBetaFeatures: () => toggleFeature('betaFeatures'),
    toggleAiAssistant: () => toggleFeature('aiAssistant'),
    // Enable/disable specific features
    enableFeature: (feature: keyof FeatureFlags) => toggleFeature(feature, true),
    disableFeature: (feature: keyof FeatureFlags) => toggleFeature(feature, false)
  }
}

/**
 * Hook for application initialization state
 * @returns Initialization state and actions
 */
export const useAppInitialization = () => {
  const { initialized, setInitialized, resetApplication } = useApplicationStore()
  
  return {
    initialized,
    setInitialized,
    resetApplication
  }
}

/**
 * Hook for complete application state
 * @returns All application state and actions
 */
export const useApplication = () => {
  const { 
    config, 
    features, 
    initialized, 
    updateConfig, 
    toggleFeature, 
    setInitialized, 
    resetApplication 
  } = useApplicationStore()
  
  return {
    config,
    features,
    initialized,
    updateConfig,
    toggleFeature,
    setInitialized,
    resetApplication
  }
}

/**
 * Selector hooks for specific configuration values
 */
export const useApiUrl = () => {
  return applicationStore((state) => state.config.apiUrl)
}

export const useAppVersion = () => {
  return applicationStore((state) => state.config.version)
}

export const useEnvironment = () => {
  return applicationStore((state) => state.config.environment)
}

export const useMaintenanceMode = () => {
  return applicationStore((state) => state.config.maintenanceMode)
}

export const useSupportEmail = () => {
  return applicationStore((state) => state.config.supportEmail)
}

/**
 * Selector hooks for specific feature flags
 */
export const useDarkModeFeature = () => {
  return applicationStore((state) => state.features.darkMode)
}

export const useAdvancedSearchFeature = () => {
  return applicationStore((state) => state.features.advancedSearch)
}

export const useAnalyticsFeature = () => {
  return applicationStore((state) => state.features.analytics)
}

export const useBetaFeatures = () => {
  return applicationStore((state) => state.features.betaFeatures)
}

export const useAiAssistantFeature = () => {
  return applicationStore((state) => state.features.aiAssistant)
}

/**
 * Utility hook to check if the app is in development mode
 * @returns Boolean indicating if app is in development
 */
export const useIsDevelopment = () => {
  return applicationStore((state) => state.config.environment === 'development')
}

/**
 * Utility hook to check if the app is in production mode
 * @returns Boolean indicating if app is in production
 */
export const useIsProduction = () => {
  return applicationStore((state) => state.config.environment === 'production')
}

/**
 * Utility hook to check if a feature is enabled
 * @param feature - Feature flag to check
 * @returns Boolean indicating if feature is enabled
 */
export const useIsFeatureEnabled = (feature: keyof FeatureFlags) => {
  return applicationStore((state) => state.features[feature])
}

/**
 * Utility function to initialize the application with environment variables
 * This should be called once during app startup
 */
export const initializeApplication = () => {
  const store = applicationStore.getState()
  
  // Update config with environment variables
  store.updateConfig({
    apiUrl: import.meta.env.VITE_API_URL || store.config.apiUrl,
    version: import.meta.env.VITE_APP_VERSION || store.config.version,
    environment: (import.meta.env.NODE_ENV as AppConfig['environment']) || store.config.environment
  })
  
  // Mark as initialized
  store.setInitialized(true)
}